import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Phone } from "lucide-react";
import CtaButtons from "./CtaButtons";
import SplitGraphic from "./SplitGraphic";
import { TRUST, CONTACT } from "./data";

const headlineLines = [
  { text: "20+ Years Keeping", gold: false },
  { text: "Central Texas", gold: true },
  { text: "Comfortable", gold: false },
];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.13 },
  }),
};

function Marquee() {
  const items = [...TRUST, ...TRUST];
  return (
    <div className="relative overflow-hidden border-y border-[#9AA3AF]/15 bg-[#101317] py-5">
      <div className="flex w-max animate-marquee whitespace-nowrap">
        {items.map((t, i) => (
          <div key={i} className="flex items-center">
            <span className="px-8 text-sm md:text-base tracking-[0.15em] uppercase text-[#9AA3AF]">
              {t}
            </span>
            <span className="text-[#C9A44C]">&#9670;</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.0, 1.08]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section id="home" ref={ref} className="relative bg-[#101317] overflow-hidden">
      {/* Full-bleed hero */}
      <div className="relative min-h-[92vh] flex items-center">
        {/* Background photo with parallax */}
        <motion.div
          style={{ scale: bgScale, y: bgY }}
          className="absolute inset-0 z-0"
        >
          <img
            src="/hero-home-clean.png"
            alt="Aerial view of a modern Central Texas home and landscape at dusk"
            className="w-full h-full object-cover object-center"
            style={{ filter: "brightness(1.28) contrast(1.02) saturate(1.05)" }}
          />
        </motion.div>

        {/* Light wash so the photo reads bright, fading toward soft light grey */}
        <div
          className="absolute inset-0 z-[1]"
          style={{ background: "linear-gradient(to bottom, rgba(200,205,212,0.06) 0%, rgba(154,163,175,0.04) 60%, rgba(16,19,23,0.30) 100%)" }}
        />
        {/* Localized soft scrim behind the text block on the left only */}
        <div
          className="absolute inset-0 z-[1]"
          style={{
            background:
              "linear-gradient(to right, rgba(16,19,23,0.72) 0%, rgba(16,19,23,0.5) 26%, rgba(16,19,23,0.2) 46%, rgba(16,19,23,0.05) 60%, rgba(16,19,23,0) 72%)",
          }}
        />

        {/* Content */}
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 lg:px-10 pt-28 pb-16">
          <div className="max-w-2xl">
            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-[1.08] font-bold" style={{ textShadow: "0 2px 20px rgba(16,19,23,0.75)" }}>
              {headlineLines.map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span
                    className={`block ${line.gold ? "text-[#C9A44C]" : "text-white"}`}
                    variants={lineVariant}
                    custom={i}
                    initial="hidden"
                    animate="visible"
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            {/* Subhead */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-6 max-w-xl text-lg md:text-xl text-white/90 leading-relaxed"
              style={{ textShadow: "0 1px 14px rgba(16,19,23,0.7)" }}
            >
              Fast, honest HVAC service without the upsell. Residential and commercial heating and
              cooling done right the first time.
            </motion.p>

            {/* Actions: 3 CTAs + Call ghost button */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <CtaButtons size="lg" />
              <a
                data-testid="hero-call-btn"
                href={CONTACT.phoneHref}
                className="focus-gold px-8 py-4 rounded-full font-semibold tracking-wide text-white border border-[#9AA3AF]/50 bg-[#101317]/40 backdrop-blur-sm inline-flex items-center gap-2 hover:border-[#C9A44C] hover:text-[#C9A44C] hover:-translate-y-0.5 transition-[border-color,color,transform] duration-300"
              >
                <Phone size={18} /> Call (512) 740-4818
              </a>
            </motion.div>

            {/* Trust line with stars */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="mt-8 flex items-center gap-3"
            >
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={18} className="fill-[#C9A44C] text-[#C9A44C]" />
                ))}
              </div>
              <span className="text-[#9AA3AF] text-sm md:text-base">
                Trusted by Central Texas homeowners for more than two decades.
              </span>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Trust strip (kept directly beneath hero) */}
      <Marquee />

      {/* Cooling & Heating split feature band */}
      <SplitGraphic />

      {/* Welcome section */}
      <div className="relative z-10 max-w-[1000px] mx-auto px-5 lg:px-10 py-24 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-xs tracking-[0.25em] uppercase text-[#9AA3AF]">Welcome</span>
          <h2 className="mt-3 text-3xl md:text-4xl text-[#C9A44C]">
            A local name Central Texas has trusted for decades.
          </h2>
          <div className="mt-8 space-y-6 text-white/85 text-lg leading-relaxed">
            <p>
              Million-Air A/C and Heating is an established residential and light-commercial HVAC
              company rooted right here in Round Rock, Texas. For more than two decades we have kept
              Central Texas families and small businesses comfortable through blistering summers and
              the occasional hard freeze, one honest service call at a time.
            </p>
            <p>
              Our roots are local and our reputation is personal. We grew by doing careful,
              respectful work in our neighbors' homes and businesses, earning an A+ rating from the
              Better Business Bureau and accreditation that dates back to 2005. As an American
              Standard Dealer, we pair premium equipment with the kind of straightforward advice you
              would want from a trusted friend in the trade.
            </p>
            <p>
              We believe comfort should feel effortless and dealing with your HVAC company should
              feel just as easy. No pressure, no gimmicks, just quality work done right and standing
              behind it. That is the standard our name has always carried, and it is the standard we
              bring to every job.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
